// Indicateurs de parc (`GET /maintenance/kpis`) recalculés sur l'appareil.
// Port de backend/app/core/maintenance_kpis.py, vérifié par les jeux communs
// offline_parity/maintenance_kpis.json.

const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};

// Une chaîne sans fuseau est de l'UTC, comme côté serveur.
export function lireInstant(valeur) {
  if (!valeur) return null;
  let texte = String(valeur).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) texte += 'T00:00:00Z';
  else if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(texte)) texte += 'Z';
  const t = Date.parse(texte);
  return Number.isNaN(t) ? null : t;
}

// Python arrondit à égalité vers le pair (round(2.5) == 2): même résultat ici.
export function arrondir(valeur, decimales = 0) {
  const f = 10 ** decimales;
  const y = valeur * f;
  const bas = Math.floor(y);
  const reste = y - bas;
  let r;
  if (Math.abs(reste - 0.5) < 1e-9) r = bas % 2 === 0 ? bas : bas + 1;
  else r = Math.round(y);
  return r / f;
}

const HEURE = 3600000;

export function distanceSurLaFenetre(releves, debut, fin) {
  const parCamion = {};
  releves.forEach((r) => {
    if (r.reading_type !== undefined && r.reading_type !== null && r.reading_type !== 'km') return;
    const t = lireInstant(r.reading_date);
    if (t === null || t < debut || t > fin) return;
    if (!r.truck_id) return;
    (parCamion[r.truck_id] = parCamion[r.truck_id] || []).push(num(r.value));
  });
  return Object.values(parCamion).reduce(
    (total, valeurs) => (valeurs.length >= 2 ? total + (Math.max(...valeurs) - Math.min(...valeurs)) : total),
    0,
  );
}

export function calculerIndicateursEntretien({
  immobilisations = [], ordres = [], pannes = [], releves = [], plans = [], papiers = [],
  joursPeriode = 30, maintenant = new Date(),
}) {
  const jours = Math.max(1, Math.min(Number.parseInt(joursPeriode || 30, 10) || 30, 3650));
  const fin = new Date(maintenant).getTime();
  const debut = fin - jours * 24 * HEURE;
  const debutIso = new Date(debut).toISOString();
  const finIso = new Date(fin).toISOString();

  let heuresAuSol = 0;
  const heuresReparation = [];
  const camions = new Set();
  immobilisations.forEach((ligne) => {
    const commence = lireInstant(ligne.started_at) ?? lireInstant(ligne.created_at);
    if (commence === null) return;
    let libere = lireInstant(ligne.released_at);
    if (libere === null && ligne.status === 'released') libere = fin;
    const termine = libere ?? fin;
    if (termine < debut || commence > fin) return;
    const debutRecouvrement = Math.max(commence, debut);
    const finRecouvrement = Math.min(termine, fin);
    if (finRecouvrement > debutRecouvrement) heuresAuSol += (finRecouvrement - debutRecouvrement) / HEURE;
    if (ligne.truck_id) camions.add(ligne.truck_id);
    if (libere !== null && libere >= debut) heuresReparation.push(Math.max(0, (libere - commence) / HEURE));
  });
  [...ordres, ...pannes].forEach((ligne) => {
    if (ligne.truck_id) camions.add(ligne.truck_id);
  });

  const tailleDuParc = camions.size;
  const heuresDisponibles = tailleDuParc * jours * 24;
  const tauxImmobilisation = heuresDisponibles ? arrondir((heuresAuSol / heuresDisponibles) * 100, 2) : 0;
  const mttr = heuresReparation.length
    ? arrondir(heuresReparation.reduce((a, b) => a + b, 0) / heuresReparation.length, 2)
    : null;

  const dansLaFenetre = (valeur) => {
    const t = lireInstant(valeur);
    return t !== null && t >= debut && t <= fin;
  };
  const ordresDeLaPeriode = ordres.filter((o) => dansLaFenetre(o.completed_at || o.created_at));
  const coutTotal = ordresDeLaPeriode.reduce((s, o) => s + num(o.total_cost), 0);
  const coutParType = {};
  ordresDeLaPeriode.forEach((o) => {
    const cle = o.work_type || 'corrective';
    coutParType[cle] = (coutParType[cle] || 0) + num(o.total_cost);
  });

  const distance = distanceSurLaFenetre(releves, debut, fin);
  const coutAuKm = distance ? arrondir(coutTotal / distance, 2) : null;

  const pannesDeLaPeriode = pannes.filter((p) => dansLaFenetre(p.reported_at || p.created_at));
  const nombrePannes = pannesDeLaPeriode.length;
  const mtbfKm = nombrePannes && distance ? arrondir(distance / nombrePannes, 0) : null;
  const joursDeService = tailleDuParc * jours;
  const mtbfJours = nombrePannes && joursDeService ? arrondir(joursDeService / nombrePannes, 1) : null;

  let echeances = plans.filter((p) => ['overdue', 'due_soon'].includes(p.due_state)).length;
  echeances += papiers.filter((d) => ['expired', 'expiring'].includes(d.expiry_state)).length;

  return {
    period_days: jours,
    period_start: debutIso,
    period_end: finIso,
    fleet_size: tailleDuParc,
    immobilization_rate: tauxImmobilisation,
    immobilized_hours: arrondir(heuresAuSol, 2),
    cost_per_km: coutAuKm,
    distance_km: distance ? arrondir(distance, 1) : 0,
    mtbf_km: mtbfKm,
    mtbf_days: mtbfJours,
    mttr_hours: mttr,
    upcoming_deadlines_count: echeances,
    immobilized_trucks: new Set(immobilisations.filter((i) => i.status === 'active' && i.truck_id).map((i) => i.truck_id)).size,
    open_work_orders: ordres.filter((o) => ['draft', 'planned', 'in_progress'].includes(o.status)).length,
    open_breakdowns: pannes.filter((p) => ['open', 'acknowledged'].includes(p.status)).length,
    breakdowns_in_period: nombrePannes,
    cost_total: arrondir(coutTotal, 2),
    cost_by_type: Object.fromEntries(Object.entries(coutParType).map(([cle, valeur]) => [cle, arrondir(valeur, 2)])),
  };
}
